import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import {
  categoryLabel,
  conditionLabel,
  destinationLabel,
  statusLabel,
} from '../../lib/taxonomy/options';
import type { ItemFilters } from '../../lib/query/itemFilters';

type ChipKey = keyof ItemFilters | 'search';

/**
 * Removable chips for whatever's currently narrowing the gallery. Room is
 * intentionally excluded — it has its own RoomTabs strip.
 */
export function ActiveFilterChips({
  filters,
  search,
  onRemove,
  onClearSearch,
}: {
  filters: ItemFilters;
  search: string;
  onRemove: (key: keyof ItemFilters) => void;
  onClearSearch: () => void;
}) {
  const { t } = useTranslation();
  const chips: { key: ChipKey; label: string; remove: () => void }[] = [];

  if (search.trim())
    chips.push({ key: 'search', label: `“${search.trim()}”`, remove: onClearSearch });
  if (filters.category)
    chips.push({ key: 'category', label: categoryLabel(filters.category, t), remove: () => onRemove('category') });
  if (filters.destination)
    chips.push({ key: 'destination', label: destinationLabel(filters.destination, t), remove: () => onRemove('destination') });
  if (filters.progressStatus)
    chips.push({ key: 'progressStatus', label: statusLabel(filters.progressStatus, t), remove: () => onRemove('progressStatus') });
  if (filters.condition)
    chips.push({ key: 'condition', label: conditionLabel(filters.condition, t), remove: () => onRemove('condition') });
  if (filters.fragile) chips.push({ key: 'fragile', label: t('item.fragile'), remove: () => onRemove('fragile') });
  if (filters.boxId === 'none') chips.push({ key: 'boxId', label: t('item.noBox'), remove: () => onRemove('boxId') });
  if (filters.missingPhoto)
    chips.push({ key: 'missingPhoto', label: t('gallery.missingPhoto'), remove: () => onRemove('missingPhoto') });

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {chips.map((c) => (
        <button
          key={c.key}
          type="button"
          onClick={c.remove}
          className="inline-flex items-center gap-1 rounded-full bg-petrol-tint text-petrol-deep border border-petrol/20 ps-2.5 pe-1.5 py-1 text-[13px] font-medium hover:bg-petrol/15"
        >
          {c.label}
          <X className="size-3.5" />
        </button>
      ))}
    </div>
  );
}
